'use client';

import React, { useState } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import Link from 'next/link';
import parse from 'html-react-parser';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import { ReCaptchaV3Provider } from './components/ReCaptchaV3Provider';
import { ValidationRules } from './components/FormValidation';
import { Checkbox } from "@/components/ui/checkbox";
import { useUtmTracking } from '@/hooks/useUtmTracking';
import { baseFormOptions, getStatesForCountry, shouldUseTextInput, getStateFieldLabel } from './steps/baseFormOptions';

// Help center content configuration - Easy to update and maintain
const helpCentreContent = {
  'logging-in': {
    title: "Can't access your account?",
    message: "Visit our <a href='https://help.octetfinance.com/' target='_blank'>Help Centre</a> for step-by-step instructions. If you're still locked out, contact your Account or Relationship Manager."
  },
  'password': {
    title: "Forgotten your password or stuck with 2FA?",
    message: "Quickly resolve this through our <a href='https://help.octetfinance.com/' target='_blank'>Help Centre</a>. Most access issues can be fixed in minutes."
  },
  'existing-client': {
    title: "Already a client?",
    message: "Log into your <a href='https://app.octet.com/' target='_blank'>Octet account</a> and use the in-platform chat for priority support. Or reach out to your Account or Relationship Manager."
  },
  'supplier': {
    title: "Invited to Octet?",
    message: "If you’re a supplier invited by a buyer to join Octet, follow the link in your email invitation. For payment or order questions, please log in and visit our <a href='https://help.octetfinance.com/' target='_blank'>Help Centre</a>."
  },
  'products': {
    title: "Looking to use Octet’s finance solutions?",
    message: "Use our <a href='/enquire'>Product Enquiry Form</a> to choose the right product or facility. This ensures we connect you with the right team."
  },
  'broker': {
    title: "Partnering with us?",
    message: "Use our <a href='/enquire/join-partner-program'>Referral Partner Form</a> to register and access a dedicated BDM, program rewards and tailored support."
  },
  'transaction': {
    title: "Need help with a transaction?",
    message: "Log into your <a href='https://app.octet.com/' target='_blank'>Octet account</a> and use the in-platform support options for real-time help. For suppliers, contact the client who invited you."
  },
  'invoice': {
    title: "Have an invoice-related question?",
    message: "Ensure your client has invited you to the Octet platform. If they have, <a href='https://app.octet.com/' target='_blank'>log in</a> to view payment details or contact them directly."
   },
  'complaint': {
    title: "Want to share feedback?",
    message: "Let us know how we can improve. If you're an existing client, please reach out to your Account Manager or use this form."
  },
  'partnership': {
    title: "Want to collaborate with us?",
    message: "Visit our <a href='/referral-partners'>Partnership Page</a> or submit your idea using this form."
  },
  'career': {
    title: "Looking to join the Octet team?",
    message: "View open roles and apply directly via our <a href='/careers'>Careers Page</a>."
  },
  'other': {
    title: "Not sure where your question fits?",
    message: "Explore our <a href='https://help.octetfinance.com/' target='_blank'>Help Centre</a> or continue with this form and our team will guide your request to the right place."
  }
};

// Form data interface
interface ContactFormData {
  firstName: string;
  lastName: string;
  businessName: string;
  phone: string;
  email: string;
  country: string;
  state: string;
  topic: string;
  message: string;
  marketingConsent: boolean;
}

// Topic options
const topicOptions = [
  { value: 'logging-in', label: 'Trouble logging in' },
  { value: 'password', label: 'Reset password or 2FA issue' },
  { value: 'existing-client', label: 'I’m an existing Octet client and need help' },
  { value: 'supplier', label: 'I’ve been invited by a client (I’m a supplier)' },
  { value: 'products', label: 'I’m enquiring about Octet’s products' },
  { value: 'broker', label: 'I’m enquiring for a client (I’m a broker, adviser or accountant)' },
  { value: 'transaction', label: 'Issue with a transaction' },
  { value: 'invoice', label: 'Questions about an invoice' },
  { value: 'complaint', label: 'Feedback or complaint' },
  { value: 'partnership', label: 'Partnership or business proposal' },
  { value: 'career', label: 'Applying for a role / career' },
  { value: 'other', label: 'Other / Not sure' },
];

const getTopLabelText = (topic: string) => {
  const option = topicOptions.find(opt => opt.value === topic);
  return option?.label || topic;
};

const getInterestedService = (topic: string) => {
  switch (topic) {
    case 'logging-in':
      return 'Enquiry - Operations';
    case 'password':
      return 'Enquiry - Operations';
    case 'existing-client':
      return 'Enquiry - Operations';
    case 'supplier':
      return 'Enquiry - Sales and Product';
    case 'products':
      return 'Enquiry - Sales and Product';
    case 'broker':
      return 'Enquiry - Sales and Product';
    case 'transaction':
      return 'Enquiry - Operations';
    case 'invoice':
      return 'Enquiry - Operations';
    case 'complaint':
      return 'Enquiry - Marketing';
    case 'partnership':
      return 'Enquiry - Marketing';
    case 'career':
      return 'Enquiry - HR';
    case 'other':
      return 'General Enquiry';
    default:
      return 'General Enquiry';
  }
};

interface ContactSupportFormProps {
  onSubmit?: (data: ContactFormData) => void | Promise<void>;
  className?: string;
  // Builder.io configuration props
  title?: string;
  description?: string;
  submitButtonText?: string;
  showHelpCentreInfo?: boolean;
  privacyPolicyUrl?: string;
  formId?: string;
}

// Original component without reCAPTCHA (for backward compatibility)
export const ContactSupportForm: React.FC<ContactSupportFormProps> = (props) => {
  return <ContactSupportFormWithCaptcha {...props} />;
};


// Inner component that uses reCAPTCHA hooks
const ContactSupportFormInner: React.FC<ContactSupportFormProps> = ({
  onSubmit,
  title,
  description,
  submitButtonText = "Submit",
  showHelpCentreInfo = true,
  privacyPolicyUrl = "/privacy-policy",
  formId = "contact-support-form"
}) => {
  const form = useForm<ContactFormData>({
    defaultValues: {
      firstName: '',
      lastName: '',
      businessName: '',
      phone: '',
      email: '',
      country: 'Australia',
      state: '',
      topic: '',
      message: '',
      marketingConsent: false,
    },
    mode: 'onChange', // Validate on change
  });

  const { isSubmitting, isValid } = form.formState;
  const { control } = form;
  const [showThankYou, setShowThankYou] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [, setRecaptchaToken] = useState<string | null>(null);
  
  const { executeRecaptcha } = useGoogleReCaptcha();
  const utmParams = useUtmTracking();

  // Watch country field for dynamic state selection
  const selectedCountry = useWatch({ 
    control, 
    name: "country" as never 
  }) as unknown as string | undefined;

  // Dynamic state logic based on selected country
  const dynamicStates = selectedCountry ? getStatesForCountry(selectedCountry) : [];
  const useTextInput = selectedCountry ? shouldUseTextInput(selectedCountry) : false;
  const stateFieldLabel = selectedCountry ? getStateFieldLabel(selectedCountry) : "State";

  // Clear state only when country actually changes to a different value
  const prevCountryRef = React.useRef<string | undefined>(undefined);
  
  React.useEffect(() => {
    if (prevCountryRef.current !== undefined && prevCountryRef.current !== selectedCountry) {
      form.setValue('state', '');
    }
    prevCountryRef.current = selectedCountry;
  }, [selectedCountry, form]);

  // Execute reCAPTCHA v3 - get token only (10s timeout, 1 retry on failure; verification happens server-side)
  const executeRecaptchaV3 = async (): Promise<{ token: string } | null> => {
    if (!executeRecaptcha) {
      console.warn('reCAPTCHA v3 not ready - executeRecaptcha is undefined');
      return null;
    }

    const RECAPTCHA_TIMEOUT_MS = 10000;

    const attempt = async (): Promise<{ token: string }> => {
      const timeoutPromise = new Promise<never>((_, reject) =>
        setTimeout(() => reject(new Error('reCAPTCHA timeout after 10 seconds')), RECAPTCHA_TIMEOUT_MS)
      );
      const token = await Promise.race([executeRecaptcha('contact_form_submit'), timeoutPromise]) as string;
      if (!token) throw new Error('No token');
      setRecaptchaToken(token);
      return { token };
    };

    for (let i = 0; i < 2; i++) {
      try {
        return await attempt();
      } catch (error) {
        if (i === 1) {
          console.error('reCAPTCHA v3 execution failed:', error);
          setSubmitError('Security verification failed. Please try again.');
          return null;
        }
      }
    }
    return null;
  };

  const handleSubmit = async (data: ContactFormData) => {
    try {
      // Clear any previous errors
      setSubmitError(null);
      
      // Execute reCAPTCHA v3 first
      const captchaResult = await executeRecaptchaV3();
      if (!captchaResult) {
        setSubmitError('Security verification failed. Please try again.');
        return;
      }

      // Proceed with form submission
      if (onSubmit) {
        await onSubmit(data);
      } else {
        // Map form data to Dynamics field format

        const dynamicsPayload = {
          ot_topic: 'Contact Us',
          ot_firstname: data.firstName,
          ot_lastname: data.lastName,
          ot_businessname: data.businessName,
          ot_phone: data.phone,
          ot_workemail: data.email,
          ot_country: data.country,
          ot_state: data.state,
          ot_interestedservice: getInterestedService(data.topic),
          pb_inquirytopic: getTopLabelText(data.topic),
          ot_yourenquiry: data.message,
          ...utmParams,
          ot_subscribedtonewsletter: data.marketingConsent ? 'true' : 'false',
          // Include reCAPTCHA token (will be verified server-side)
          recaptchaToken: captchaResult.token,
        };
        
        // Submit to Dynamics via API route
        const response = await fetch('/api/dynamics', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(dynamicsPayload),
        });

        const result = await response.json();

        if (!response.ok) {
          throw new Error(result.message || result.error || 'Failed to submit form');
        }

        console.info('Form submitted successfully:', result);
      }
      
      // Show thank you content after successful submission
      setShowThankYou(true);
      
      // Scroll to top of the page
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (error) {
      console.error('Form submission error:', error);
      setSubmitError(error instanceof Error ? error.message : 'Failed to submit form. Please try again.');
    }
  };

  return (
    <div className="bg-white border border-border rounded-xl shadow-lg px-6 py-10" id={formId}>
      {showThankYou ? (
        // Thank You Content
        <div className="text-center">
          {/* Success Icon */}
          <div className="flex justify-center mb-6">
            <span className="material-symbols-outlined font-variation-filled text-green-600 size-12" style={{ fontSize: '48px' }}>
              check_circle
            </span>
          </div>

          {/* Main Heading */}
          <h3 className=" mb-4">
            Thank you
          </h3>

          {/* Confirmation Message */}
          <p className="paragraph-lg mb-12">
            We&apos;ve received your message and our team will be in touch soon.
          </p>

          {/* Follow-up Suggestion */}
          <p className="mb-4 paragraph-base">
            While you wait, why not check out our latest guides and insights?
          </p>

          {/* Call-to-Action Button */}
 
            <Button 
              asChild
              size="lg"
              variant="outline" 
              className="w-full border-[#0A66C2] text-[#0A66C2] hover:bg-[#0A66C2] hover:text-white transition-colors"
            >
              <Link href="https://www.linkedin.com/company/octet" target="_blank" rel="noopener noreferrer">
                Follow Octet on LinkedIn
              </Link>
            </Button>
 
        </div>
      ) : (
        // Form Content
        <>
          {/* Optional title and description */}
          {title && (
            <div className="text-center mb-6">
              <h2 className="text-2xl font-bold text-foreground mb-2">{title}</h2>
              {description && (
                <p className="text-muted-foreground">{description}</p>
              )}
            </div>
          )}

          {/* Error Message */}
          {submitError && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
              <div className="flex gap-2">
                <span className="material-symbols-outlined text-red-500">error</span>
                <div>
                  <p className="text-red-500 font-bold">Submission Error</p>
                  <p className="text-red-500">Having trouble? You can <Link href="/contact-us">contact support</Link> or try refreshing the page.</p>
                </div>
              </div>
            </div>
          )}

          <Form {...form}>
            <form onSubmit={form.handleSubmit(handleSubmit)} className="flex flex-col gap-6">
              {/* Name Fields - Two columns */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 items-start">
                <FormField
                  control={form.control}
                  name="firstName"
                  rules={ValidationRules.required('First name')}
                  render={({ field }) => (
                    <FormItem className="flex flex-col">
                      <FormLabel >First Name</FormLabel>
                      <FormControl>
                        <Input placeholder="" {...field} maxLength={500} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                
                <FormField
                  control={form.control}
                  name="lastName"
                  rules={ValidationRules.required('Last name')}
                  render={({ field }) => (
                    <FormItem className="flex flex-col">
                      <FormLabel >Last Name</FormLabel>
                      <FormControl>
                        <Input placeholder="" {...field} maxLength={500} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              {/* Business Name - Full width */}
              <FormField
                control={form.control}
                name="businessName"
                rules={ValidationRules.businessName()}
                render={({ field }) => (
                  <FormItem className="flex flex-col">
                    <FormLabel >Business Name</FormLabel>
                    <FormControl>
                      <Input placeholder="" {...field} maxLength={500} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* Contact Fields - Two columns */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 items-start">
                <FormField
                  control={form.control}
                  name="phone"
                  rules={ValidationRules.phone()}
                  render={({ field }) => (
                    <FormItem className="flex flex-col">
                      <FormLabel >Phone</FormLabel>
                      <FormControl>
                        <Input type="tel" placeholder="" {...field} maxLength={100} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                
                <FormField
                  control={form.control}
                  name="email"
                  rules={ValidationRules.email()}
                  render={({ field }) => (
                    <FormItem className="flex flex-col">
                      <FormLabel >Email</FormLabel>
                      <FormControl>
                        <Input type="email" placeholder="" {...field} maxLength={500} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 items-start">
                {/* Country Field */}
                <FormField
                  control={form.control}
                  name="country"
                  rules={ValidationRules.selection('country')}
                  render={({ field }) => (
                    <FormItem className="flex flex-col">
                      <FormLabel >Country</FormLabel>
                      <Select onValueChange={field.onChange} value={field.value}>
                        <FormControl>
                          <SelectTrigger className="w-full">
                            <SelectValue placeholder="Select country" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {baseFormOptions.allCountries.map((country) => (
                            <SelectItem key={country} value={country}>
                              {country}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                {/* State Field - Dynamic based on country */}
                <FormField
                  control={form.control}
                  name="state"
                  rules={{
                    required: useTextInput 
                      ? `Please enter a ${stateFieldLabel.toLowerCase()}`
                      : `Please select a ${stateFieldLabel.toLowerCase()}`,
                    validate: (value: unknown) => {
                      // State is always required
                      const stringValue = typeof value === 'string' ? value : String(value || '');
                      if (!value || stringValue.trim() === '') {
                        return useTextInput
                          ? `Please enter a ${stateFieldLabel.toLowerCase()}`
                          : `Please select a ${stateFieldLabel.toLowerCase()}`;
                      }
                      return true; // Valid if value is selected
                    }
                  }}
                  render={({ field }) => (
                    <FormItem className="flex flex-col">
                      <FormLabel>{stateFieldLabel}</FormLabel>
                      <FormControl>
                        {useTextInput ? (
                          <Input
                            placeholder=""
                            value={(field.value as string) || ""}
                            onChange={field.onChange}
                            maxLength={100}
                          />
                        ) : (
                          <Select
                            value={(field.value as string) || ""}
                            onValueChange={(value) => { field.onChange(value); }}
                          >
                            <SelectTrigger className="w-full">
                              <SelectValue placeholder="Select" />
                            </SelectTrigger>
                            <SelectContent>
                              {dynamicStates.map((s: { code: string; name: string }) => (
                                <SelectItem key={s.code} value={s.code}>{s.name}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        )}
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              {/* Topic Selection */}
              <div className="grid grid-cols-1">
                <FormField
                  control={form.control}
                  name="topic"
                  rules={ValidationRules.selection('topic')}
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel >Select Topic</FormLabel>
                      <Select onValueChange={field.onChange} defaultValue={field.value}>
                        <FormControl>
                          <SelectTrigger className="w-full">
                            <SelectValue placeholder="Select" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {topicOptions.map((option) => (
                            <SelectItem key={option.value} value={option.value}>
                              {option.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              {/* Help Centre Information Block - Conditional */}
              {(showHelpCentreInfo && form.watch('topic')) && (
                <div className="bg-muted rounded-lg py-4 px-6">
                  {(() => {
                    const selectedTopic = form.watch('topic');
                    const content = helpCentreContent[selectedTopic as keyof typeof helpCentreContent] || helpCentreContent['other'];
                    
                    return (
                      <>
                        {content.title && <strong className="block mb-2">{content.title}</strong>}
                        <p className="text-sm text-muted-foreground">
                          {parse(content.message)}
                        </p>
                      </>
                    );
                  })()}
                </div>
              )}

              {/* Message */}
              <FormField
                control={form.control}
                name="message"
                rules={ValidationRules.required('Message')}
                render={({ field }) => {
                  const messageValue = field.value || '';
                  const characterCount = messageValue.length;
                  const maxLength = 4000;
                  
                  return (
                    <FormItem>
                      <FormLabel >Message</FormLabel>
                      <FormControl>
                        <Textarea 
                          placeholder="" 
                          className="min-h-32"
                          maxLength={maxLength}
                          {...field} 
                        />
                      </FormControl>
                      <div className="flex justify-between items-center">
                        <FormMessage />
                        <p className="paragraph-sm text-muted-foreground ml-auto">
                          {characterCount} / {maxLength}
                        </p>
                      </div>
                    </FormItem>
                  );
                }}
              />

              <FormField
                control={form.control}
                name={"marketingConsent" as never}
                render={({ field }) => (
                  <FormItem>
                    {/* <FormLabel>Marketing Consent</FormLabel> */}
                    <FormControl>
                      <label htmlFor="marketingConsent" className="flex items-center gap-3 cursor-pointer">
                        <Checkbox
                          id="marketingConsent"
                          checked={!!field.value}
                          onCheckedChange={(checked) => { field.onChange(checked); }}
                          className="cursor-pointer"
                        />
                        <p className="paragraph-sm leading-relaxed font-normal">
                          I&apos;d like to receive occasional news and updates from Octet by email. <span className="font-normal text-muted-foreground">(Optional)</span>
                        </p>
                      </label>
                    </FormControl>
                  </FormItem>
                )}
              />

              {/* Submit Button */}
              <Button 
                type="submit" 
                variant="primary"
                size="lg"
                disabled={isSubmitting || !isValid}
                className="w-full mb-2"
              >
                {isSubmitting ? 'Submitting...' : submitButtonText}
              </Button>
              
              {/* Privacy Information */}
              <p className="paragraph-sm text-muted-foreground text-center">
                For more information about how we handle your personal information, please visit our{' '}
                <Link href={privacyPolicyUrl} className="text-primary hover:underline">
                  privacy policy
                </Link>
                . This site and form is protected by reCAPTCHA.
              </p>
            </form>
          </Form>
        </>
      )}
    </div>
  );
};

// Wrapper component that provides reCAPTCHA v3 context
export const ContactSupportFormWithCaptcha: React.FC<ContactSupportFormProps> = (props) => {
  return (
    <ReCaptchaV3Provider>
      <ContactSupportFormInner {...props} />
    </ReCaptchaV3Provider>
  );
};
